import { useCallback, useEffect, useMemo, useState } from 'react';

type Sponsor = { id: string; name: string; destination_url: string; status: string };
type Slot = { code: string; name: string; surface: string };
type Campaign = {
  id: string;
  sponsor_name: string;
  slot_code: string;
  name: string;
  status: 'draft' | 'active' | 'paused' | 'ended';
  scope_type: 'country' | 'region' | 'global';
  countries: string | null;
  global_exclusive: number;
};
type AdminData = { sponsors: Sponsor[]; slots: Slot[]; campaigns: Campaign[] };
type Metric = {
  campaign_id: string;
  campaign_name: string;
  sponsor_name: string;
  slot_code: string;
  country_code: string;
  impressions: number;
  clicks: number;
};

const isoInput = (offsetDays: number) => {
  const date = new Date(Date.now() + offsetDays * 86400000);
  date.setMinutes(date.getMinutes() - date.getTimezoneOffset());
  return date.toISOString().slice(0, 16);
};

export function SponsorshipAdmin({ authenticated }: { authenticated: boolean }) {
  const [data, setData] = useState<AdminData | null>(null);
  const [metrics, setMetrics] = useState<Metric[]>([]);
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [previewCountry, setPreviewCountry] = useState('EC');
  const [preview, setPreview] = useState<
    Array<{ slot: string; sponsorName: string | null; campaignName: string | null }>
  >([]);
  const [sponsor, setSponsor] = useState({ name: '', destinationUrl: '', logoUrl: '' });
  const [campaign, setCampaign] = useState({
    sponsorId: '',
    slotCode: 'arena_center',
    name: '',
    scopeType: 'country' as 'country' | 'region' | 'global',
    countries: 'EC',
    excludedCountries: '',
    regionCode: 'LATAM',
    priority: 0,
    globalExclusive: false,
    startsAt: isoInput(0),
    endsAt: isoInput(30),
  });
  const [prize, setPrize] = useState({
    cupSlug: '',
    title: '',
    description: '',
    amount: '',
    currency: 'USD',
    sponsorId: '',
  });

  const load = useCallback(async () => {
    if (!authenticated) return;
    const response = await fetch('/api/v1/admin/sponsorships', { cache: 'no-store' });
    if (response.status === 403) return;
    if (!response.ok) return setMessage('No fue posible abrir la administración comercial.');
    const next = (await response.json()) as AdminData;
    setData(next);
    setCampaign((current) => ({
      ...current,
      sponsorId: current.sponsorId || next.sponsors[0]?.id || '',
      slotCode: current.slotCode || next.slots[0]?.code || '',
    }));
    const metricResponse = await fetch('/api/v1/admin/sponsorships/metrics', { cache: 'no-store' });
    if (metricResponse.ok)
      setMetrics(((await metricResponse.json()) as { metrics: Metric[] }).metrics);
  }, [authenticated]);

  useEffect(() => {
    void load();
  }, [load]);

  const totals = useMemo(
    () =>
      metrics.reduce(
        (sum, item) => ({
          impressions: sum.impressions + item.impressions,
          clicks: sum.clicks + item.clicks,
        }),
        { impressions: 0, clicks: 0 },
      ),
    [metrics],
  );

  async function send(url: string, method: 'POST' | 'PATCH', body: unknown) {
    setBusy(true);
    setMessage(null);
    try {
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const result = (await response.json()) as { detail?: string; id?: string };
      if (!response.ok) throw new Error(result.detail ?? 'No fue posible completar la acción.');
      await load();
      return result;
    } catch (cause) {
      setMessage(cause instanceof Error ? cause.message : 'No fue posible completar la acción.');
      return null;
    } finally {
      setBusy(false);
    }
  }

  if (!data) return null;

  return (
    <section id="patrocinios-admin" className="mt-24 lg:mt-32">
      <p className="eyebrow">Operación comercial</p>
      <div className="mt-3 flex flex-wrap items-end justify-between gap-5">
        <div>
          <h2 className="text-3xl font-black text-white sm:text-5xl">
            Patrocinios por territorio.
          </h2>
          <p className="mt-3 max-w-3xl text-sm leading-6 text-zinc-400">
            Cada espacio conserva su lugar. El motor elige una sola marca por país: exclusiva
            global, local, regional y global, en ese orden.
          </p>
        </div>
        <div className="rounded-2xl border border-emerald-300/15 bg-emerald-300/5 px-5 py-3 text-right text-xs text-emerald-100">
          <strong className="block text-lg text-white">
            {totals.impressions.toLocaleString()}
          </strong>
          impresiones · {totals.clicks.toLocaleString()} clics
        </div>
      </div>

      {message && (
        <p
          role="status"
          className="mt-5 rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-zinc-300"
        >
          {message}
        </p>
      )}

      <div className="mt-7 grid gap-5 xl:grid-cols-3">
        <form
          className="glass-panel rounded-[2rem] p-6"
          onSubmit={async (event) => {
            event.preventDefault();
            const result = await send('/api/v1/admin/sponsors', 'POST', {
              ...sponsor,
              logoUrl: sponsor.logoUrl || null,
            });
            if (result) {
              setSponsor({ name: '', destinationUrl: '', logoUrl: '' });
              setMessage('Patrocinador creado.');
            }
          }}
        >
          <h3 className="text-lg font-bold text-white">1. Nueva marca</h3>
          <label className="field-label mt-5">
            Nombre
            <input
              className="field-input mt-2"
              required
              value={sponsor.name}
              onChange={(e) => setSponsor({ ...sponsor, name: e.target.value })}
            />
          </label>
          <label className="field-label mt-4">
            Sitio HTTPS
            <input
              className="field-input mt-2"
              type="url"
              required
              value={sponsor.destinationUrl}
              onChange={(e) => setSponsor({ ...sponsor, destinationUrl: e.target.value })}
            />
          </label>
          <label className="field-label mt-4">
            Logo HTTPS (opcional)
            <input
              className="field-input mt-2"
              type="url"
              value={sponsor.logoUrl}
              onChange={(e) => setSponsor({ ...sponsor, logoUrl: e.target.value })}
            />
          </label>
          <button
            disabled={busy}
            className="mt-5 w-full rounded-full bg-white px-5 py-3 text-sm font-bold text-zinc-950 disabled:opacity-50"
          >
            Guardar marca
          </button>
        </form>

        <form
          className="glass-panel rounded-[2rem] p-6 xl:col-span-2"
          onSubmit={async (event) => {
            event.preventDefault();
            const result = await send('/api/v1/admin/sponsor-campaigns', 'POST', {
              ...campaign,
              countries: campaign.countries
                .split(',')
                .map((v) => v.trim().toUpperCase())
                .filter(Boolean),
              excludedCountries: campaign.excludedCountries
                .split(',')
                .map((v) => v.trim().toUpperCase())
                .filter(Boolean),
              regionCode: campaign.scopeType === 'region' ? 'LATAM' : null,
              startsAt: new Date(campaign.startsAt).toISOString(),
              endsAt: new Date(campaign.endsAt).toISOString(),
            });
            if (result?.id) {
              await send(`/api/v1/admin/sponsor-campaigns/${result.id}/status`, 'PATCH', {
                status: 'active',
                confirmGlobalExclusive: campaign.globalExclusive,
                reason: campaign.globalExclusive
                  ? 'Activación confirmada de contrato global exclusivo.'
                  : undefined,
              });
              setCampaign({ ...campaign, name: '' });
              setMessage('Campaña creada y activada.');
            }
          }}
        >
          <h3 className="text-lg font-bold text-white">2. Nueva campaña</h3>
          <div className="mt-5 grid gap-4 sm:grid-cols-2">
            <label className="field-label">
              Marca
              <select
                className="field-input mt-2"
                required
                value={campaign.sponsorId}
                onChange={(e) => setCampaign({ ...campaign, sponsorId: e.target.value })}
              >
                <option value="">Selecciona</option>
                {data.sponsors.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="field-label">
              Espacio
              <select
                className="field-input mt-2"
                value={campaign.slotCode}
                onChange={(e) => setCampaign({ ...campaign, slotCode: e.target.value })}
              >
                {data.slots.map((item) => (
                  <option key={item.code} value={item.code}>
                    {item.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="field-label sm:col-span-2">
              Nombre interno
              <input
                className="field-input mt-2"
                required
                value={campaign.name}
                onChange={(e) => setCampaign({ ...campaign, name: e.target.value })}
                placeholder="Marathon Ecuador · Arena · Octubre"
              />
            </label>
            <label className="field-label">
              Alcance
              <select
                className="field-input mt-2"
                value={campaign.scopeType}
                onChange={(e) =>
                  setCampaign({
                    ...campaign,
                    scopeType: e.target.value as typeof campaign.scopeType,
                  })
                }
              >
                <option value="country">Países específicos</option>
                <option value="region">Región LATAM</option>
                <option value="global">Global</option>
              </select>
            </label>
            {campaign.scopeType === 'country' ? (
              <label className="field-label">
                Países (ISO, separados por coma)
                <input
                  className="field-input mt-2"
                  value={campaign.countries}
                  onChange={(e) => setCampaign({ ...campaign, countries: e.target.value })}
                  placeholder="EC,CO,PE"
                />
              </label>
            ) : (
              <label className="field-label">
                Excluir países (opcional)
                <input
                  className="field-input mt-2"
                  value={campaign.excludedCountries}
                  onChange={(e) => setCampaign({ ...campaign, excludedCountries: e.target.value })}
                  placeholder="CU,VE"
                />
              </label>
            )}
            <label className="field-label">
              Inicio
              <input
                className="field-input mt-2"
                type="datetime-local"
                required
                value={campaign.startsAt}
                onChange={(e) => setCampaign({ ...campaign, startsAt: e.target.value })}
              />
            </label>
            <label className="field-label">
              Fin
              <input
                className="field-input mt-2"
                type="datetime-local"
                required
                value={campaign.endsAt}
                onChange={(e) => setCampaign({ ...campaign, endsAt: e.target.value })}
              />
            </label>
          </div>
          {campaign.scopeType === 'global' && (
            <label className="mt-4 flex items-center gap-3 text-sm text-zinc-300">
              <input
                type="checkbox"
                checked={campaign.globalExclusive}
                onChange={(e) => setCampaign({ ...campaign, globalExclusive: e.target.checked })}
              />{' '}
              Contrato global exclusivo: reemplaza campañas locales
            </label>
          )}
          <button
            disabled={busy || !data.sponsors.length}
            className="mt-5 w-full rounded-full bg-fuchsia-300 px-5 py-3 text-sm font-black text-zinc-950 disabled:opacity-50"
          >
            Crear y activar campaña
          </button>
        </form>
      </div>

      <div className="mt-6 overflow-hidden rounded-[2rem] border border-white/8">
        <div className="grid grid-cols-[1.4fr_1fr_1fr_auto] gap-3 bg-white/5 px-5 py-3 text-[0.65rem] font-bold uppercase tracking-wider text-zinc-500">
          <span>Campaña</span>
          <span>Espacio</span>
          <span>Alcance</span>
          <span>Estado</span>
        </div>
        {data.campaigns.map((item) => (
          <div
            key={item.id}
            className="grid grid-cols-[1.4fr_1fr_1fr_auto] items-center gap-3 border-t border-white/7 px-5 py-4 text-xs text-zinc-300"
          >
            <span>
              <strong className="block text-white">{item.sponsor_name}</strong>
              {item.name}
            </span>
            <span>{item.slot_code}</span>
            <span>{item.scope_type === 'country' ? item.countries : item.scope_type}</span>
            <button
              type="button"
              onClick={() => {
                const activating = item.status !== 'active';
                if (
                  activating &&
                  item.global_exclusive === 1 &&
                  !window.confirm(
                    'Esta campaña reemplazará las marcas locales en todos los países. ¿Confirmas la exclusiva mundial?',
                  )
                )
                  return;
                void send(`/api/v1/admin/sponsor-campaigns/${item.id}/status`, 'PATCH', {
                  status: activating ? 'active' : 'paused',
                  confirmGlobalExclusive: activating && item.global_exclusive === 1,
                  reason:
                    item.global_exclusive === 1
                      ? 'Cambio confirmado de campaña global exclusiva.'
                      : undefined,
                });
              }}
              className="rounded-full border border-white/10 px-3 py-1.5"
            >
              {item.status}
            </button>
          </div>
        ))}
      </div>

      <div className="mt-6 rounded-[2rem] border border-cyan-300/15 bg-cyan-300/[0.03] p-6">
        <div className="flex flex-wrap items-end gap-3">
          <label className="field-label flex-1">
            Vista previa comercial por país
            <input
              className="field-input mt-2"
              maxLength={2}
              value={previewCountry}
              onChange={(event) => setPreviewCountry(event.target.value.toUpperCase())}
            />
          </label>
          <button
            type="button"
            className="rounded-full bg-cyan-100 px-5 py-3 text-sm font-bold text-zinc-950"
            onClick={async () => {
              const slots = data.slots.map((item) => item.code).join(',');
              const response = await fetch(
                `/api/v1/admin/sponsorships/preview?country=${encodeURIComponent(previewCountry)}&slots=${encodeURIComponent(slots)}`,
              );
              if (response.ok)
                setPreview(((await response.json()) as { placements: typeof preview }).placements);
            }}
          >
            Simular país
          </button>
        </div>
        <div className="mt-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
          {preview.map((item) => (
            <div
              key={item.slot}
              className="rounded-xl border border-white/8 p-3 text-xs text-zinc-400"
            >
              <strong className="block text-white">{item.slot}</strong>
              {item.sponsorName || 'Sin patrocinador'}
              {item.campaignName ? ` · ${item.campaignName}` : ''}
            </div>
          ))}
          {preview.length === 0 && (
            <p className="text-xs text-zinc-500">
              La simulación no registra impresiones y permite verificar qué marca verá cada país.
            </p>
          )}
        </div>
      </div>

      <form
        className="mt-6 grid gap-4 rounded-[2rem] border border-amber-300/15 bg-amber-300/[0.04] p-6 sm:grid-cols-2"
        onSubmit={async (event) => {
          event.preventDefault();
          const result = await send(
            `/api/v1/admin/cups/${encodeURIComponent(prize.cupSlug)}/prizes`,
            'POST',
            {
              title: prize.title,
              description: prize.description,
              amountMinor: prize.amount ? Math.round(Number(prize.amount) * 100) : null,
              currency: prize.amount ? prize.currency : null,
              sponsorId: prize.sponsorId || null,
            },
          );
          if (result) {
            setPrize({ ...prize, title: '', description: '', amount: '' });
            setMessage('Premio anunciado; la entrega permanece manual y auditable.');
          }
        }}
      >
        <div className="sm:col-span-2">
          <h3 className="text-lg font-bold text-white">Premio manual de Copa</h3>
          <p className="mt-1 text-xs text-zinc-500">
            Se anuncia ahora; el destinatario y la entrega pueden confirmarse después.
          </p>
        </div>
        <label className="field-label">
          Slug de Copa
          <input
            className="field-input mt-2"
            required
            value={prize.cupSlug}
            onChange={(e) => setPrize({ ...prize, cupSlug: e.target.value })}
          />
        </label>
        <label className="field-label">
          Patrocinador
          <select
            className="field-input mt-2"
            value={prize.sponsorId}
            onChange={(e) => setPrize({ ...prize, sponsorId: e.target.value })}
          >
            <option value="">Sin marca asociada</option>
            {data.sponsors.map((item) => (
              <option key={item.id} value={item.id}>
                {item.name}
              </option>
            ))}
          </select>
        </label>
        <label className="field-label">
          Título
          <input
            className="field-input mt-2"
            required
            value={prize.title}
            onChange={(e) => setPrize({ ...prize, title: e.target.value })}
          />
        </label>
        <label className="field-label">
          Monto opcional
          <div className="mt-2 grid grid-cols-[1fr_7rem] gap-2">
            <input
              className="field-input"
              type="number"
              min="0"
              step="0.01"
              value={prize.amount}
              onChange={(e) => setPrize({ ...prize, amount: e.target.value })}
            />
            <input
              className="field-input"
              maxLength={3}
              value={prize.currency}
              onChange={(e) => setPrize({ ...prize, currency: e.target.value.toUpperCase() })}
            />
          </div>
        </label>
        <label className="field-label sm:col-span-2">
          Descripción
          <textarea
            className="field-input mt-2 min-h-20"
            required
            value={prize.description}
            onChange={(e) => setPrize({ ...prize, description: e.target.value })}
          />
        </label>
        <button
          disabled={busy}
          className="rounded-full bg-amber-200 px-5 py-3 text-sm font-black text-zinc-950 sm:col-span-2"
        >
          Anunciar premio
        </button>
      </form>
      <p className="mt-4 text-xs text-zinc-600">
        Las métricas comerciales son agregadas por día, país y espacio. Nunca incluyen correos,
        nombres reales ni el historial individual de un usuario.
      </p>
    </section>
  );
}
